/**
 * ResultScreen.tsx - Shows group results for the Risky Choice Framing experiment with translation support and updated design
 * Adapted: 2025-08-14
 * Adapted by: JulianPolly
 *
 * Changes:
 * - Introduced useLanguage translation pattern for all static texts and option/legend labels.
 * - Removed all hardcoded German texts; replaced with translation keys and language context.
 * - Adjusted all text, card, and chart styles to use a new, modern palette, matching the more branded look.
 * - Harmonized font choices, font sizes, card radius, and spacing to be more visually consistent.
 * - Unified legend block and result message styling.
 * - Tooltip now shows percentage as in the working example.
 * - Chart and legend blocks are visually balanced and accessible.
 */

import React, { useEffect, useState, useCallback } from "react";
import { Bar, BarChart, Tooltip, XAxis, YAxis, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { RiskyChoiceFramingKey } from "./RiskyChoiceFramingSequence";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, chartColors, commonStyles } from "../../../../components/colors";

// Colors for chart bars
const safeColor = chartColors.primary[0]; // e.g., blue-green
const riskyColor = chartColors.primary[1]; // e.g., blue

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    background: colors.backgroundLight,
    margin: 0,
    minWidth: 470,
    maxWidth: 650,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 260,
    padding: 28,
    borderRadius: 16,
    boxShadow: "0 2px 12px rgba(30,60,120,0.10)"
};

const chartBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    gap: 24,
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8
};

const legendStyle: React.CSSProperties = {
    fontSize: 15,
    color: colors.textMedium,
    marginTop: 16,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    gap: 8
};

const legendItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10
};

const legendColorBoxStyle = (color: string): React.CSSProperties => ({
    width: 18,
    height: 18,
    backgroundColor: color,
    display: "inline-block",
    marginRight: 8,
    borderRadius: 4,
    border: `1px solid ${colors.silver}`
});

const resultMessageStyle: React.CSSProperties = {
    ...commonStyles.card,
    marginTop: 36,
    textAlign: "center",
    fontSize: 18,
    background: colors.backgroundLight,
    maxWidth: 800,
    marginLeft: "auto",
    marginRight: "auto",
    color: colors.textDark,
    borderRadius: 12,
    fontWeight: 500,
    gap: 14
};

const names = {
    kletterpark: { sicher: "Wipfelreich", riskant: "Gipfelglück" },
    schwimmbad: { sicher: "Sprungbach", riskant: "Wellenhain" }
};

// Translation keys for all texts used
const texts = {
    de: {
        title: "Ergebnisse deiner Klasse – So hat deine Klasse bei den Entscheidungsszenarien abgestimmt.",
        kletterparkFrame: "Gewinn-Rahmen (Gain-Frame)",
        schwimmbadFrame: "Verlustrahmen (Loss-Frame)",
        kletterpark: "Kletterpark-Entscheidung",
        schwimmbad: "Schwimmbad-Entscheidung",
        kletterparkSub: "Die Klasse kann zwischen einem sicheren Ausflug und einer riskanten Alternative wählen. Hier siehst du, wie sich deine Klasse entschieden hat.",
        schwimmbadSub: "Beim zweiten Entscheidungsszenario (Schwimmbad) standen dieselben Wahlmöglichkeiten zur Verfügung. So hat deine Klasse entschieden.",
        legendSafe: "Sichere Option",
        legendRisky: "Riskante Option",
        undecided: (frame: string) => `Im <b>${frame}</b> konntest du dich nicht entscheiden oder hast keine Auswahl getroffen.`,
        decided: (frame: string, optionType: string, optionName: string) =>
            `Im <b>${frame}</b> hast du dich für die <b>${optionType === "sicher" ? "sichere" : "riskante"} Option</b> (<b>${optionName}</b>) entschieden.`,
        loading: "Lade Ergebnisse…"
    },
    en: {
        title: "Your class results – Here’s how your class voted in the two decision scenarios.",
        kletterparkFrame: "Gain frame",
        schwimmbadFrame: "Loss frame",
        kletterpark: "Climbing Park Decision",
        schwimmbad: "Swimming Pool Decision",
        kletterparkSub: "The class could choose between a safe trip and a risky alternative. Here you can see what your class chose.",
        schwimmbadSub: "In the second scenario (swimming pool) the same options were available. Here’s your class’s decision.",
        legendSafe: "Safe option",
        legendRisky: "Risky option",
        undecided: (frame: string) => `In the <b>${frame}</b> you did not make a selection.`,
        decided: (frame: string, optionType: string, optionName: string) =>
            `In the <b>${frame}</b> you chose the <b>${optionType === "sicher" ? "safe" : "risky"} option</b> (<b>${optionName}</b>).`,
        loading: "Loading results…"
    }
};

// Custom Tooltip with percentage
const CustomTooltip = ({ active, payload, label, total }: any) => {
    if (active && payload && payload.length) {
        const value = payload[0].value;
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
        return (
            <div style={{
                background: "#fff",
                border: `1px solid ${colors.silver}`,
                padding: 12,
                borderRadius: 8,
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)"
            }}>
                <div style={{
                    fontWeight: 600,
                    marginBottom: 6,
                    color: colors.brillantBlue
                }}>
                    {label}
                </div>
                <div style={{ color: payload[0].color }}>
                    {value} ({percentage}%)
                </div>
            </div>
        );
    }
    return null;
};

export const ResultScreenComponent = () => {
    const { getGroupData, userData } = useSession();
    const { language } = useLanguage();
    const t = texts[language];

    // Chart data and state
    const [kletterData, setKletterData] = useState<any[]>([]);
    const [schwimmbadData, setSchwimmbadData] = useState<any[]>([]);
    const [kletterTotal, setKletterTotal] = useState(0);
    const [schwimmbadTotal, setSchwimmbadTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    // User decisions for both scenarios
    let userKletterChoice: number | undefined = undefined;
    let userSchwimmbadChoice: number | undefined = undefined;

    // Get user decisions from userData
    if (userData && userData[RiskyChoiceFramingKey]) {
        const riskyObj = userData[RiskyChoiceFramingKey];
        if (riskyObj["1"]?.selections?.length > 0) {
            userKletterChoice = riskyObj["1"].selections[riskyObj["1"].selections.length - 1].choice;
        }
        if (riskyObj["2"]?.selections?.length > 0) {
            userSchwimmbadChoice = riskyObj["2"].selections[riskyObj["2"].selections.length - 1].choice;
        }
    }

    // Helper for translated frame-label
    const getFrameLabel = useCallback((frame: "kletterpark" | "schwimmbad") =>
        frame === "kletterpark" ? t.kletterparkFrame : t.schwimmbadFrame, [t]);

    // Helper for user result sentence
    const getDecisionHtml = useCallback((frame: "kletterpark" | "schwimmbad", choice?: number) => {
        if (choice === undefined) {
            return t.undecided(getFrameLabel(frame));
        }
        let optionType: "sicher" | "riskant";
        if (choice === 1) optionType = "sicher";
        else if (choice === 2) optionType = "riskant";
        else return t.undecided(getFrameLabel(frame));
        return t.decided(getFrameLabel(frame), optionType, names[frame][optionType]);
    }, [t, getFrameLabel]);

    // Chart group data loading
    useEffect(() => {
        setLoading(true);
        getGroupData().then(data => {
            const kletterCounts = { sicher: 0, riskant: 0 };
            const schwimmbadCounts = { sicher: 0, riskant: 0 };

            data?.forEach((dat: any) => {
                const riskyObj = dat[RiskyChoiceFramingKey];
                if (riskyObj) {
                    // Kletterpark ("1")
                    const kletterItem = riskyObj["1"];
                    if (kletterItem?.selections?.length > 0) {
                        const lastSelection = kletterItem.selections[kletterItem.selections.length - 1];
                        if (lastSelection.choice === 1) kletterCounts.sicher += 1;
                        if (lastSelection.choice === 2) kletterCounts.riskant += 1;
                    }
                    // Schwimmbad ("2")
                    const schwimmbadItem = riskyObj["2"];
                    if (schwimmbadItem?.selections?.length > 0) {
                        const lastSelection = schwimmbadItem.selections[schwimmbadItem.selections.length - 1];
                        if (lastSelection.choice === 1) schwimmbadCounts.sicher += 1;
                        if (lastSelection.choice === 2) schwimmbadCounts.riskant += 1;
                    }
                }
            });

            const totalK = kletterCounts.sicher + kletterCounts.riskant;
            const totalS = schwimmbadCounts.sicher + schwimmbadCounts.riskant;
            setKletterTotal(totalK);
            setSchwimmbadTotal(totalS);

            setKletterData([
                { name: t.legendSafe, description: names.kletterpark.sicher, Anzahl: kletterCounts.sicher, color: safeColor },
                { name: t.legendRisky, description: names.kletterpark.riskant, Anzahl: kletterCounts.riskant, color: riskyColor }
            ]);
            setSchwimmbadData([
                { name: t.legendSafe, description: names.schwimmbad.sicher, Anzahl: schwimmbadCounts.sicher, color: safeColor },
                { name: t.legendRisky, description: names.schwimmbad.riskant, Anzahl: schwimmbadCounts.riskant, color: riskyColor }
            ]);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [getGroupData, t]);

    // Custom legend as in the working example
    const renderLegend = (data: any[]) => (
        <div style={legendStyle}>
            {data.map(({ name, description, color }) => (
                <div key={name} style={legendItemStyle}>
                    <span style={legendColorBoxStyle(color)}></span>
                    <span>
                        <b>{name}</b>: {description}
                    </span>
                </div>
            ))}
        </div>
    );

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>
            {t.loading}
        </div>;
    }

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t.title}
            </h2>
            <div style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 48,
                marginTop: 24
            }}>
                {/* Kletterpark */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10,
                        textAlign: "center"
                    }}>
                        {t.kletterpark}
                    </h3>
                    <div style={{
                        fontStyle: "italic",
                        marginBottom: 14,
                        color: colors.textMedium
                    }}>
                        {t.kletterparkSub}
                    </div>
                    <div style={chartBlockStyle}>
                        <div style={{ minWidth: 180, maxWidth: 550, flex: 1, margin: "0 auto" }}>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={kletterData}>
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 14 }}
                                        interval={0}
                                        height={50}
                                    />
                                    <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                    <Tooltip
                                        content={<CustomTooltip total={kletterTotal} />}
                                        cursor={{ fill: 'rgba(200, 200, 200, 0.15)' }}
                                    />
                                    <Bar dataKey="Anzahl" barSize={40}>
                                        {kletterData.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList
                                            dataKey="Anzahl"
                                            position="top"
                                            fontSize={14}
                                            fill={colors.brillantBlue}
                                            fontWeight={600}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                            {renderLegend(kletterData)}
                        </div>
                    </div>
                </div>
                {/* Schwimmbad */}
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10,
                        textAlign: "center"
                    }}>
                        {t.schwimmbad}
                    </h3>
                    <div style={{
                        fontStyle: "italic",
                        marginBottom: 14,
                        color: colors.textMedium
                    }}>
                        {t.schwimmbadSub}
                    </div>
                    <div style={chartBlockStyle}>
                        <div style={{ minWidth: 180, maxWidth: 550, flex: 1, margin: "0 auto" }}>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={schwimmbadData}>
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 14 }}
                                        interval={0}
                                        height={50}
                                    />
                                    <YAxis allowDecimals={false} domain={[0, 'dataMax + 1']} />
                                    <Tooltip
                                        content={<CustomTooltip total={schwimmbadTotal} />}
                                        cursor={{ fill: 'rgba(200, 200, 200, 0.15)' }}
                                    />
                                    <Bar dataKey="Anzahl" barSize={40}>
                                        {schwimmbadData.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                        <LabelList
                                            dataKey="Anzahl"
                                            position="top"
                                            fontSize={14}
                                            fill={colors.brillantBlue}
                                            fontWeight={600}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                            {renderLegend(schwimmbadData)}
                        </div>
                    </div>
                </div>
            </div>
            {/* User result message for both frames */}
            <div
                style={resultMessageStyle}
            >
                <div dangerouslySetInnerHTML={{ __html: getDecisionHtml("kletterpark", userKletterChoice) }} />
                <div dangerouslySetInnerHTML={{ __html: getDecisionHtml("schwimmbad", userSchwimmbadChoice) }} />
            </div>
        </div>
    );
};

export const ResultScreen: ScreenConfig = {
    component: ResultScreenComponent,
    defaultResult: {
        selections: [],
        reasoning: "",
    },
    canContinueDefault: true,
};